import type { Witness } from '../store/booksStore'
import { getRowLabel, type AnchoredRow } from '../types/anchoredRowTypes'
import type { LedgerSafeEditorialEvent } from '../types/ledgerSafeEditorialEvents'
import {
  applyLedgerSafeMergeUnitsToSegmentation,
  createLedgerSafeUnitSplitContext,
  prepareLedgerSafeWitnessContext,
  previewLedgerSafeBoundaryShift,
  previewLedgerSafeUnitSplit
} from './ledgerSafeBoundaryEditing'
import {
  buildLedgerSafeSnapshotSplitContext,
  extractLedgerSafeBoundarySymptom,
  targetStillShowsLedgerSafeBoundarySymptom
} from './ledgerSafeEditSuggestionBoundary'
import {
  canSuggestLedgerSafeMappedRows,
  normalizeLedgerSafeSuggestionRowIds
} from './ledgerSafeEditSuggestionEvents'
import {
  findLedgerSafeNextMappedRowId,
  getLedgerSafeSuggestionWitnessLabel
} from './ledgerSafeEditSuggestionRows'
import {
  describeLedgerSafeSuggestionContentMatch,
  MIN_LEDGER_SAFE_WINDOW_CONTENT_SCORE,
  scoreLedgerSafeWitnessWindowSimilarity
} from './ledgerSafeEditSuggestionScoring'
import type { LedgerSafeEditSuggestion } from './ledgerSafeEditSuggestionTypes'
import { locateLedgerSafeSplitSuggestion } from './ledgerSafeSplitSuggestionEngine'

export const canPreviewLedgerSafeBoundaryShift = ({
  rows,
  witness,
  leftRowId,
  deltaWords
}: {
  rows: AnchoredRow[]
  witness: Witness
  leftRowId: string
  deltaWords: number
}): boolean => {
  if (!witness.segmentation_result || !witness.uploaded_file?.content || !leftRowId || deltaWords === 0) {
    return false
  }
  const prepared = prepareLedgerSafeWitnessContext({
    rows,
    witnessId: witness.id,
    segmentationResult: witness.segmentation_result,
    sourceText: witness.uploaded_file.content
  })
  const context = prepared?.getBoundaryContext(leftRowId) || null
  if (!context) {
    return false
  }
  try {
    const preview = previewLedgerSafeBoundaryShift({
      context,
      sourceText: prepared?.sourceText || witness.uploaded_file.content,
      deltaWords,
      segmentableWordSpans: prepared?.segmentableWordSpans
    })
    return preview.deltaWords === deltaWords
  } catch {
    return false
  }
}

export const buildLedgerSafeBoundaryShiftSuggestion = ({
  event,
  rows,
  targetWitness,
  sourceWitness,
  leftRowId,
  rightRowId,
  deltaWords
}: {
  event: LedgerSafeEditorialEvent
  rows: AnchoredRow[]
  targetWitness: Witness
  sourceWitness: Witness | undefined
  leftRowId: string
  rightRowId: string | null
  deltaWords: number
}): LedgerSafeEditSuggestion | null => {
  if (!rightRowId) {
    return null
  }
  const sourceWitnessId = sourceWitness?.id || ''
  const contentScore = sourceWitnessId
    ? scoreLedgerSafeWitnessWindowSimilarity({
      rows,
      rowIds: [leftRowId, rightRowId],
      sourceWitnessId,
      targetWitnessId: targetWitness.id
    })
    : 0
  const symptom = sourceWitnessId
    ? extractLedgerSafeBoundarySymptom({ event, sourceWitnessId, leftRowId, rightRowId, deltaWords })
    : null
  if (symptom && !targetStillShowsLedgerSafeBoundarySymptom({
    rows,
    targetWitnessId: targetWitness.id,
    leftRowId,
    rightRowId,
    symptom
  })) {
    return null
  }
  if (!symptom && contentScore < MIN_LEDGER_SAFE_WINDOW_CONTENT_SCORE) {
    return null
  }
  // Full witness preparation is costly on a large chapter. Only perform it
  // after the same cheap content/symptom checks have admitted this candidate.
  if (!canPreviewLedgerSafeBoundaryShift({ rows, witness: targetWitness, leftRowId, deltaWords })) {
    return null
  }
  const sourceLabel = getLedgerSafeSuggestionWitnessLabel(sourceWitness, 'another witness')
  const targetLabel = getLedgerSafeSuggestionWitnessLabel(targetWitness, targetWitness.id)
  const finalContentScore = symptom ? Math.max(contentScore, 0.74) : contentScore
  return {
    id: `${event.id}:boundary_shift:${targetWitness.id}:${leftRowId}:${deltaWords}`,
    kind: 'boundary_shift',
    sourceEventId: event.id,
    sourceWitnessId: sourceWitness?.id || '',
    targetWitnessId: targetWitness.id,
    rowIds: [leftRowId, rightRowId],
    primaryRowId: leftRowId,
    params: {
      witnessId: targetWitness.id,
      leftRowId,
      deltaWords
    },
    label: `Move boundary ${deltaWords > 0 ? '+' : ''}${deltaWords}`,
    detail: `${targetLabel} · ${deltaWords > 0 ? 'move text down' : 'move text up'}`,
    reason: `Suggested because you made the same boundary correction in ${sourceLabel}. ${describeLedgerSafeSuggestionContentMatch(finalContentScore)}`,
    confidence: 'high',
    contentScore: finalContentScore
  }
}

export const buildLedgerSafeSplitUnitSuggestion = ({
  event,
  rows,
  targetWitness,
  sourceWitness,
  rowId,
  afterWordIndex
}: {
  event: LedgerSafeEditorialEvent
  rows: AnchoredRow[]
  targetWitness: Witness
  sourceWitness: Witness | undefined
  rowId: string
  afterWordIndex: number
}): LedgerSafeEditSuggestion | null => {
  if (
    !sourceWitness?.segmentation_result ||
    !sourceWitness.uploaded_file?.content ||
    !targetWitness.segmentation_result ||
    !targetWitness.uploaded_file?.content
  ) {
    return null
  }

  const snapshotSource = buildLedgerSafeSnapshotSplitContext({
    event,
    witnessId: sourceWitness.id,
    rowId
  })
  const sourceContext = snapshotSource?.context || createLedgerSafeUnitSplitContext({
    rows,
    witnessId: sourceWitness.id,
    rowId,
    segmentationResult: sourceWitness.segmentation_result,
    sourceText: sourceWitness.uploaded_file.content
  })
  const targetContext = createLedgerSafeUnitSplitContext({
    rows,
    witnessId: targetWitness.id,
    rowId,
    segmentationResult: targetWitness.segmentation_result,
    sourceText: targetWitness.uploaded_file.content
  })
  if (!sourceContext || !targetContext) {
    return null
  }

  const localization = locateLedgerSafeSplitSuggestion({
    sourceContext,
    targetContext,
    sourceSplitBoundaryWordIndex: snapshotSource?.splitBoundaryWordIndex || afterWordIndex
  })
  if (!localization) {
    return null
  }

  try {
    const preview = previewLedgerSafeUnitSplit({
      context: targetContext,
      sourceText: targetContext.sourceText,
      splitBoundaryWordIndex: localization.afterWordIndex
    })
    if (
      preview.splitBoundaryWordIndex !== localization.afterWordIndex ||
      !preview.currentText.trim() ||
      !preview.newFragmentText.trim()
    ) {
      return null
    }
  } catch {
    return null
  }

  const sourceLabel = getLedgerSafeSuggestionWitnessLabel(sourceWitness, 'another witness')
  const targetLabel = getLedgerSafeSuggestionWitnessLabel(targetWitness, targetWitness.id)
  const row = rows.find(candidate => candidate.id === rowId)
  return {
    id: `${event.id}:split_unit:${targetWitness.id}:${rowId}:${localization.afterWordIndex}`,
    kind: 'split_unit',
    sourceEventId: event.id,
    sourceWitnessId: sourceWitness.id,
    targetWitnessId: targetWitness.id,
    rowIds: [rowId],
    primaryRowId: rowId,
    params: {
      witnessId: targetWitness.id,
      rowId,
      afterWordIndex: localization.afterWordIndex,
      sourceAfterWordIndex: afterWordIndex,
      sourceBoundaryOffset: localization.sourceBoundaryOffset,
      targetBoundaryOffset: localization.targetBoundaryOffset,
      targetTokenCount: targetContext.words.filter(word => word.segmentableWordIndex >= 0).length,
      targetMinAfterWordIndex: targetContext.minSplitBoundaryWordIndex,
      targetMaxAfterWordIndex: targetContext.maxSplitBoundaryWordIndex,
      splitLocalizationScore: localization.score,
      splitLocalizationMargin: localization.margin,
      sourceLeftText: localization.sourceLeftText,
      sourceRightText: localization.sourceRightText,
      targetLeftText: localization.targetLeftText,
      targetRightText: localization.targetRightText
    },
    label: `Split after word ${localization.afterWordIndex}`,
    detail: `${targetLabel} · ${row ? getRowLabel(row) : rowId}`,
    reason: `Suggested because you split the same collation row in ${sourceLabel}. Split boundary localized at ${Math.round(localization.score * 100)}%.`,
    confidence: localization.confidence,
    contentScore: localization.score
  }
}

export const canPreviewLedgerSafeMerge = ({
  rows,
  witness,
  rowIds
}: {
  rows: AnchoredRow[]
  witness: Witness
  rowIds: string[]
}): boolean => {
  if (!witness.segmentation_result || !witness.uploaded_file?.content || rowIds.length < 2) {
    return false
  }
  if (!canSuggestLedgerSafeMappedRows({ rowsById: new Map(rows.map(row => [row.id, row])), rowIds, witnessId: witness.id })) {
    return false
  }
  try {
    applyLedgerSafeMergeUnitsToSegmentation({
      rows,
      witnessId: witness.id,
      rowIds,
      segmentationResult: witness.segmentation_result,
      sourceText: witness.uploaded_file.content
    })
    return true
  } catch {
    return false
  }
}

export const buildLedgerSafeMergeUnitsSuggestion = ({
  event,
  rows,
  targetWitness,
  sourceWitness,
  rowIds
}: {
  event: LedgerSafeEditorialEvent
  rows: AnchoredRow[]
  targetWitness: Witness
  sourceWitness: Witness | undefined
  rowIds: string[]
}): LedgerSafeEditSuggestion | null => {
  if (!canPreviewLedgerSafeMerge({ rows, witness: targetWitness, rowIds })) {
    return null
  }
  const sourceWitnessId = sourceWitness?.id || ''
  const contentScore = sourceWitnessId
    ? scoreLedgerSafeWitnessWindowSimilarity({
      rows,
      rowIds,
      sourceWitnessId,
      targetWitnessId: targetWitness.id
    })
    : 0
  if (contentScore < MIN_LEDGER_SAFE_WINDOW_CONTENT_SCORE) {
    return null
  }
  const sourceLabel = getLedgerSafeSuggestionWitnessLabel(sourceWitness, 'another witness')
  const targetLabel = getLedgerSafeSuggestionWitnessLabel(targetWitness, targetWitness.id)
  return {
    id: `${event.id}:merge_units:${targetWitness.id}:${normalizeLedgerSafeSuggestionRowIds(rowIds)}`,
    kind: 'merge_units',
    sourceEventId: event.id,
    sourceWitnessId: sourceWitness?.id || '',
    targetWitnessId: targetWitness.id,
    rowIds,
    primaryRowId: rowIds[0],
    params: {
      witnessId: targetWitness.id,
      rowIds
    },
    label: `Merge ${rowIds.length} rows`,
    detail: `${targetLabel} · ${rowIds.map(rowId => {
      const row = rows.find(candidate => candidate.id === rowId)
      return row ? getRowLabel(row) : rowId
    }).join(' + ')}`,
    reason: `Suggested because ${sourceLabel} was merged across the same collation rows. ${describeLedgerSafeSuggestionContentMatch(contentScore)}`,
    confidence: 'high',
    contentScore
  }
}

export const findLedgerSafeBoundaryRightRowId = (
  rows: AnchoredRow[],
  leftRowId: string,
  targetWitnessId: string
): string | null => findLedgerSafeNextMappedRowId(rows, leftRowId, targetWitnessId)
